import { defineStore } from 'pinia'
import { type Ref, onScopeDispose, ref, watch } from 'vue'

import { i18n } from '@/i18n'
import type { Subscription } from '@/lib/firebase/subscribe'
import { createRetainer } from '@/lib/live/retainer'
import { parseCallableError } from '@/lib/firebase/errors'
import {
  deleteLibraryItem as repoDeleteLibraryItem,
  subscribeLibraryItems as repoSubscribeLibraryItems,
  uploadLibraryItem as repoUploadLibraryItem,
  LibraryUploadError,
  type DeleteLibraryItemInput,
} from '@/repositories/library'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import type { LibraryItem } from '@/types/library'
import { failed, loading, type LoadState } from '@/types/load-state'

interface LibraryScope {
  workspaceId: string
  brandId: string
}

/** One in-flight (or just-finished) upload's progress, tracked client-side until it settles. */
export interface LibraryUploadTicket {
  id: string
  fileName: string
  fraction: number
  status: 'uploading' | 'done' | 'error' | 'cancelled'
  error?: string
}

export const useLibraryStore = defineStore('library', () => {
  const items = ref<LoadState<LibraryItem[]>>(loading()) as Ref<LoadState<LibraryItem[]>>
  const uploads = ref<LibraryUploadTicket[]>([])

  let itemsSub: Subscription<LibraryItem[]> | null = null
  let stopItemsWatch: (() => void) | null = null

  const uploadCancellers = new Map<string, () => void>()

  function currentScope(): LibraryScope | null {
    const auth = useAuthStore()
    const workspaceId = auth.claims?.workspaceId
    const brandId = auth.claims?.brandId
    if (!workspaceId || !brandId) return null
    return { workspaceId, brandId }
  }

  function openItems(): void {
    if (itemsSub) return

    const scope = currentScope()
    if (!scope) {
      items.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }

    items.value = loading()
    const sub = repoSubscribeLibraryItems(scope)
    itemsSub = sub
    stopItemsWatch = watch(
      sub.state,
      (state) => {
        items.value = state
      },
      { immediate: true },
    )
  }

  function closeItems(): void {
    itemsSub?.stop()
    itemsSub = null
    stopItemsWatch?.()
    stopItemsWatch = null
    items.value = loading()
  }

  const itemsRetainer = createRetainer({ open: openItems, close: closeItems })
  onScopeDispose(itemsRetainer.dispose)

  /** Uploads a file, tracking its progress in `uploads`; resolves the new item, or `false` on failure. */
  function uploadItem(file: File): { id: string; result: Promise<LibraryItem | false> } {
    const id = crypto.randomUUID()
    const ticket: LibraryUploadTicket = {
      id,
      fileName: file.name,
      fraction: 0,
      status: 'uploading',
    }
    uploads.value = [...uploads.value, ticket]

    const handle = repoUploadLibraryItem({
      file,
      onProgress: (fraction) => {
        const current = uploads.value.find((entry) => entry.id === id)
        if (current) current.fraction = fraction
      },
    })
    uploadCancellers.set(id, handle.cancel)

    const result = (async (): Promise<LibraryItem | false> => {
      try {
        const item = await handle.result
        const current = uploads.value.find((entry) => entry.id === id)
        if (current) current.status = 'done'
        return item
      } catch (cause) {
        const message =
          cause instanceof LibraryUploadError ? cause.message : parseCallableError(cause).message
        const current = uploads.value.find((entry) => entry.id === id)
        if (current) {
          current.status = current.status === 'cancelled' ? 'cancelled' : 'error'
          current.error = message
        }
        if (current?.status !== 'cancelled') {
          useToastStore().error(i18n.global.t('media.toast.uploadFailed'), message)
        }
        return false
      } finally {
        uploadCancellers.delete(id)
      }
    })()

    return { id, result }
  }

  /** Cancels an in-flight upload. */
  function cancelUpload(id: string): void {
    const cancel = uploadCancellers.get(id)
    if (!cancel) return
    const current = uploads.value.find((entry) => entry.id === id)
    if (current) current.status = 'cancelled'
    cancel()
  }

  /** Deletes a library item for good, its storage object included. */
  async function deleteItem(itemId: string): Promise<boolean> {
    try {
      await repoDeleteLibraryItem({ itemId } satisfies DeleteLibraryItemInput)
      return true
    } catch (cause) {
      const parsed = parseCallableError(cause)
      useToastStore().error(i18n.global.t('media.toast.deleteFileFailed'), parsed.message)
      return false
    }
  }

  return {
    items,
    uploads,
    retain: itemsRetainer.retain,
    uploadItem,
    cancelUpload,
    deleteItem,
  }
})
